import * as React from "react";
import { format } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { Namespace, TranslationKey, useT, useTypedT } from "@racoon/i18n";
import { IcHighPriority, IcMinimize, IcPlus } from "@racoon/icons";
import { fromTimestamp, getEnumName } from "@racoon/lib";
import {
  Button,
  Checkbox,
  cn,
  CountBadge,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@racoon/ui";
import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import {
  getJobVolume,
  isPickable,
  jobsForDay,
  roundVolume,
  useIncidentByJob,
} from "~/shared/ui/job-picker-modal/job-picker-jobs";
import { plannedContainerCount } from "~/shared/lib/job-containers";
import { hasSeveralCoJobs } from "~/shared/lib/co-jobs";
import { JobStatusPill } from "./status-pills";

const JOB_STATUSES = [ConnectProto.JobStatus.CREATED, ConnectProto.JobStatus.NOT_COMPLETED];

/** Figma "Counter Badge" here is Surface/information-strong, not the ds variant's Surface/action. */
const CounterBadge: React.FC<{ count: number }> = ({ count }) => (
  <CountBadge variant="ds" count={count} className="!bg-ds-surface-information-strong" />
);

/** A fixed-width cell: cut to fit, the whole text in its tooltip. */
const TextCell: React.FC<{ width: string; children: string }> = ({ width, children }) => (
  <span
    className={cn(width, "shrink-0 truncate px-3 py-2 font-medium tracking-[-0.2px]")}
    title={children}
  >
    {children}
  </span>
);

const formatTime = (ts: ConnectProto.Job["twStartAt"]) => {
  const date = fromTimestamp(ts);
  return date ? format(date, "HH:mm") : undefined;
};

const timeWindowLabel = (job: ConnectProto.Job) => {
  const start = formatTime(job.twStartAt);
  const end = formatTime(job.twEndAt);
  if (!start && !end) return "—";
  return `${start ?? end} - ${end ?? start}`;
};

/**
 * Figma "Todo jobs pop up": the route sheet header's chip counting the day's unassigned
 * and missed jobs (the job picker's set), opening their list in a centred dialog. Checked rows go
 * «На маршрут» through `onToRoute`.
 */
export const UnassignedJobsPopup: React.FC<{
  orgId: bigint | undefined;
  date: Date | undefined;
  onToRoute: (jobs: ConnectProto.Job[]) => void;
}> = ({ orgId, date, onToRoute }) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();
  const [open, setOpen] = React.useState(false);
  const [checked, setChecked] = React.useState<Set<bigint>>(new Set());

  // One job per order: a missed order's newest attempt decides whether it is listed.
  const { data } = useListJobsQuery(
    { serviceProviderIds: orgId ? [orgId] : [], jobStatuses: JOB_STATUSES, onePerOrder: true },
    { skip: !orgId },
  );
  const jobs = React.useMemo(() => jobsForDay(data?.jobs ?? [], date), [data, date]);
  const { incidentByJob } = useIncidentByJob(orgId, jobs);

  // Only listed rows count: a refetch or another day leaves stale ids in `checked` harmlessly.
  const picked = jobs.filter((job) => checked.has(job.id));
  // As in the orders table: several СО jobs cannot share a trip.
  const tooManyCo = hasSeveralCoJobs(picked);

  // The chip goes once the list is empty; an open dialog closes with it rather than
  // reopening by itself when jobs come back.
  React.useEffect(() => {
    if (jobs.length === 0) setOpen(false);
  }, [jobs.length]);

  const totals = React.useMemo(
    () => ({
      containers: jobs.reduce((sum, job) => sum + plannedContainerCount(job), 0),
      volume: roundVolume(jobs.reduce((sum, job) => sum + getJobVolume(job), 0)),
    }),
    [jobs],
  );

  if (jobs.length === 0) return null;

  const toggle = (job: ConnectProto.Job) => {
    if (!isPickable(job)) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(job.id)) next.delete(job.id);
      else next.add(job.id);
      return next;
    });
  };

  const handleToRoute = () => {
    onToRoute(picked);
    setChecked(new Set());
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={t("unassignedToday")}
          className="flex h-8 shrink-0 items-center gap-2 rounded-lg border border-ds-border-error-subtle bg-ds-surface-error-subtle p-2"
        >
          <IcHighPriority className="h-4 w-4 text-ds-icon-error" />
          <CounterBadge count={jobs.length} />
        </button>
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="!flex max-h-[600px] w-[1321px] flex-col sm:!max-h-[600px] sm:!max-w-[calc(100vw-32px)] overflow-hidden !gap-0 !rounded-2xl !border-ds-border-primary !bg-ds-surface-primary !p-0"
      >
        <div className="flex shrink-0 items-center gap-4 border-b border-ds-border-primary p-4">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <IcHighPriority className="h-6 w-6 shrink-0 text-ds-icon-error" />
            <DialogTitle className="truncate !text-base !font-semibold !leading-4 tracking-[-0.01px] text-ds-text-error-subtle">
              {t("unassignedToday")}
            </DialogTitle>
            <CounterBadge count={jobs.length} />
          </div>
          <div className="flex h-8 items-stretch divide-x divide-ds-border-primary rounded-lg border border-ds-border-primary bg-ds-surface-secondary text-xs font-bold uppercase leading-4 text-ds-text-secondary">
            <span className="flex items-center px-4">
              {jobs.length} {t("applications")}
            </span>
            <span className="flex items-center px-4">
              {totals.containers} {t("containersGenitive")}
            </span>
            <span className="flex items-center px-4 normal-case">
              {t("volumeCubicMeters", { volume: totals.volume })}
            </span>
          </div>
          {/* A disabled button takes no pointer events, so the wrapper carries the reason. */}
          <span title={tooManyCo ? t("toRouteOneCoJob") : undefined}>
            <Button
              variant="dsSecondary"
              size="md"
              disabled={picked.length === 0 || tooManyCo}
              onClick={handleToRoute}
              className="disabled:!bg-ds-surface-disabled disabled:!text-ds-text-disabled disabled:!opacity-100"
            >
              <IcPlus className="h-5 w-5" />
              {t("toRoute")}
            </Button>
          </span>
          <button
            type="button"
            aria-label={tAny(Namespace.Common, "collapse")}
            title={tAny(Namespace.Common, "collapse")}
            onClick={() => setOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-ds-surface-tertiary"
          >
            <IcMinimize className="h-5 w-5 text-ds-icon-default" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-2">
          {jobs.map((job, index) => {
            const incident =
              job.status === ConnectProto.JobStatus.NOT_COMPLETED
                ? incidentByJob.get(job.id)
                : undefined;
            const incidentLabel =
              incident &&
              tAny(
                Namespace.Enum,
                getEnumName(
                  ConnectProto.DriverIncidentTypeSchema,
                  incident.type,
                ) as TranslationKey<Namespace.Enum>,
              );
            const group = job.containerGroup;
            return (
              <div
                key={String(job.id)}
                onClick={() => toggle(job)}
                className={cn(
                  "flex h-10 w-max min-w-full items-center rounded-lg text-sm leading-4 text-ds-text-body hover:bg-ds-surface-secondary",
                  isPickable(job) && "cursor-pointer",
                )}
              >
                <span className="flex w-8 shrink-0 p-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={checked.has(job.id)}
                    disabled={!isPickable(job)}
                    onCheckedChange={() => toggle(job)}
                  />
                </span>
                <span className="w-10 shrink-0 p-2 text-center">{index + 1}</span>
                <TextCell width="w-[97px]">{job.orderNumber || `Z${job.id}`}</TextCell>
                <span className="flex w-[134px] shrink-0 p-2">
                  <JobStatusPill
                    status={job.status}
                    className={
                      job.status === ConnectProto.JobStatus.CREATED
                        ? "!text-ds-text-body"
                        : undefined
                    }
                  />
                </span>
                <TextCell width="w-[125px]">
                  {tAny(
                    Namespace.Orders,
                    `types.${ConnectProto.JobType[job.type]}` as TranslationKey<Namespace.Orders>,
                  )}
                </TextCell>
                <span className="flex w-[134px] shrink-0 p-2">
                  {incident && (
                    <span
                      title={incidentLabel}
                      className={cn(
                        "w-[118px] truncate rounded-lg border px-2 py-1 text-center text-xs font-medium leading-4 tracking-[-0.01px]",
                        // EXPIRED is the system's midnight expiry, a reported problem warns.
                        incident.type === ConnectProto.DriverIncidentType.EXPIRED
                          ? "border-ds-border-error bg-ds-surface-error-subtle text-ds-text-error"
                          : "border-ds-border-warning bg-ds-surface-warning-subtle text-ds-text-warning",
                      )}
                    >
                      {incidentLabel}
                    </span>
                  )}
                </span>
                <TextCell width="w-[240px]">
                  {job.pickupLocation?.name || job.pickupLocation?.address || t("noAddress")}
                </TextCell>
                <TextCell width="w-[240px]">
                  {job.serviceReceiver?.name || group?.ownerOrganization?.name || "—"}
                </TextCell>
                <TextCell width="w-[93px]">
                  {group
                    ? `${group.containerType?.name || t("container")} (${plannedContainerCount(job)})`
                    : "—"}
                </TextCell>
                <TextCell width="w-[63px]">
                  {t("volumeCubicMeters", { volume: roundVolume(getJobVolume(job)) })}
                </TextCell>
                <TextCell width="w-[107px]">{timeWindowLabel(job)}</TextCell>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
